import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { CheckCircle, Copy, Key } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const WhatsAppAIConfigSection = () => {
  const [config, setConfig] = useState({
    zapi_instance_id: '',
    zapi_token: '',
    zapi_security_token: '',
    gemini_api_key: '',
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [maskedData, setMaskedData] = useState({
    instance: '',
    token: '',
    security: '',
    gemini: ''
  });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/whatsapp-ai-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setConfigured(true);
        setMaskedData({
          instance: response.data.zapi_instance_id_masked,
          token: response.data.zapi_token_masked,
          security: response.data.zapi_security_token_masked,
          gemini: response.data.gemini_api_key_masked
        });
        setConfig(prev => ({ ...prev, enabled: response.data.enabled }));
      }
      
      setWebhookUrl(response.data.webhook_url);
    } catch (err) {
      console.error('Error loading WhatsApp config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/master/whatsapp-ai-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Configuração salva com sucesso!');
      setConfig({
        zapi_instance_id: '',
        zapi_token: '',
        zapi_security_token: '',
        gemini_api_key: '',
        enabled: config.enabled
      });
      
      setTimeout(() => loadConfig(), 1000);
    } catch (err) {
      console.error('Error saving WhatsApp config:', err);
      toast.error('❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success('✅ URL copiada!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="animate-pulse h-32 bg-gray-800/50 rounded-lg"></div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp AI (Z-API + Gemini)
        </h2>
        <p className="text-gray-400 text-sm">
          Configure integração com Z-API e Gemini para receber pedidos via WhatsApp automaticamente
          <span className="ml-2 text-yellow-400">⚠️ Apenas para loja: teste1@gmail.com</span>
        </p>
      </div>

      {/* Webhook URL */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <label className="block text-sm font-medium text-blue-300 mb-2">
          Webhook URL (Cadastre na Z-API)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={webhookUrl}
            readOnly
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm font-mono"
          />
          <button
            onClick={copyWebhook}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors flex items-center gap-2"
          >
            {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        </div>
      </div>

      {/* Z-API Fields */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Z-API Instance ID
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.instance}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.zapi_instance_id}
              onChange={(e) => setConfig({...config, zapi_instance_id: e.target.value})}
              placeholder="Seu Instance ID"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Z-API Token
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.token}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.zapi_token}
              onChange={(e) => setConfig({...config, zapi_token: e.target.value})}
              placeholder="Seu Token"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Z-API Security Token
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.security}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.zapi_security_token}
              onChange={(e) => setConfig({...config, zapi_security_token: e.target.value})}
              placeholder="Security Token"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Gemini API Key
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.gemini}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.gemini_api_key}
              onChange={(e) => setConfig({...config, gemini_api_key: e.target.value})}
              placeholder="Sua API Key do Gemini"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>
      </div>

      {/* Enable Switch */}
      <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div>
          <label className="text-white font-medium">Ativar WhatsApp AI</label>
          <p className="text-gray-400 text-sm">Sistema receberá e processará mensagens automaticamente</p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
        />
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-blue-300 text-sm">
          ℹ️ <strong>Credenciais pré-configuradas:</strong> As credenciais já estão salvas no sistema como padrão. Você pode alterá-las aqui se necessário.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Salvando...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Salvar WhatsApp AI
            </>
          )}
        </button>

        {configured && !showTokens && (
          <button
            onClick={() => setShowTokens(true)}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all"
          >
            Atualizar
          </button>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAIConfigSection;
